import { useRef, useCallback } from 'react';

/**
 * Delay a callback by `n` milliseconds, allowing it to be canceled if desired.
 *
 * You’d want to use this over useDebounce when:
 * - You care about the FIRST event (debounce cares about most recent)
 * - You never want the first call to be delayed or interrupted (debounce waits till calls stop coming in)
 *
 * Use this for single actions that have precise timing, such as click-and-hold for a menu.
 */
function useCancellableCallback(handler, { delay = 0 }, deps) {
    const timeoutRef = useRef(null);
    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);
    const action = useCallback((...args) => {
        if (timeoutRef.current !== null)
            return; // don’t interrupt current event if there’s already one in progress
        timeoutRef.current = window.setTimeout((...innerArgs) => {
            handler(...innerArgs);
            timeoutRef.current = null;
        }, delay, ...args);
        return () => cancel(); // on teardown, always cancel in-flight event
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handler SHOULD NOT be a dep here! We’re memoizing handler on behalf of the user, with them controlling the deps
    [...deps, delay, cancel]);
    return [action, cancel];
}

export { useCancellableCallback };
//# sourceMappingURL=use-cancellable-callback.js.map
